import React, { useEffect, useState } from 'react';
import { Trans, withI18n } from '@lingui/react';
import { useDispatch } from 'react-redux';
import { Form } from 'react-final-form';

import MatchWhenRole from '../../../components/routing/MatchWhenRole';
import Loader from '../../../components/utils/Loader';
import BaseFieldsRulesEditor from './BaseFieldsRulesEditor';
import { useBaseFieldsConfig } from '../hooks/useBaseFieldsConfig';
import { setIsLoadingFromLocalStorage, setLoadedFromOthersPages } from '../../../actions/pageStateActions';

const validateBaseFields = (data) => {
    const errors = {};
    if (data.ruleStrategy === 'include_specific') {
        if (!data.selectedProperties || data.selectedProperties.length === 0) {
            errors.selectedProperties = 'profiles:validation:select_at_least_one_property';
        }
    }
    return errors;
};

const BaseFieldsEditor = ({ i18n }) => {
    const dispatch = useDispatch();
    const [showResetConfirm, setShowResetConfirm] = useState(false);

    const {
        isLoading,
        isSaving,
        isResetting,
        initialValues,
        loadConfig,
        saveConfig,
        resetToDefault,
    } = useBaseFieldsConfig(i18n);

    useEffect(() => {
        dispatch(setIsLoadingFromLocalStorage(false));
        loadConfig();

        return () => {
            dispatch(setLoadedFromOthersPages(true));
        };
    }, [dispatch, loadConfig]);

    const handleResetConfirm = async () => {
        await resetToDefault();
        setShowResetConfirm(false);
    };

    let submit;

    if (isLoading) {
        return (
            <div className="mx-1 mt-4">
                <Loader />
            </div>
        );
    }

    return (
        <>
            {showResetConfirm && (
                <div className="modal active" style={{ zIndex: 1000 }}>
                    <span className="modal-overlay" onClick={() => setShowResetConfirm(false)} />
                    <div className="modal-container" style={{ maxWidth: '450px' }}>
                        <div className="modal-header">
                            <div className="modal-title h5">
                                <Trans id="basefields:reset:title" />
                                <span className="btn btn-clear float-right" onClick={() => setShowResetConfirm(false)} />
                            </div>
                        </div>
                        <div className="modal-body">
                            <p><Trans id="basefields:reset:confirm" /></p>
                        </div>
                        <div className="modal-footer">
                            <button
                                className={`btn btn-error mr-2 ${isResetting ? 'loading' : ''}`}
                                onClick={handleResetConfirm}
                                disabled={isResetting}
                            >
                                <Trans id="basefields:reset" />
                            </button>
                            <button
                                className="btn"
                                onClick={() => setShowResetConfirm(false)}
                                disabled={isResetting}
                            >
                                <Trans id="cancel" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="mx-1 p-4 pb-[60px]">
                <div className="grid grid-cols-12 gap-1">
                    <div className="col-span-full text-center mt-2">
                        <h2 className="text-primary">
                            <Trans id="basefields:title" />
                        </h2>
                        <p className="text-gray">
                            <Trans id="basefields:description" />
                        </p>
                    </div>

                    <div className="col-span-full">
                        <Form
                            onSubmit={saveConfig}
                            validate={validateBaseFields}
                            initialValues={initialValues}
                            render={({ handleSubmit, form, submitting, values }) => {
                                submit = handleSubmit;

                                return (
                                    <form onSubmit={handleSubmit}>
                                        <BaseFieldsRulesEditor
                                            formValues={values}
                                            form={form}
                                            showParentNodes={true}
                                        />

                                        <div className="divider mt-4" />
                                        <div className="text-right mt-2 fixed bottom-0 pt-2 pr-4 pb-[60px] right-0 w-full bg-white">
                                            <button
                                                type="submit"
                                                className={`btn btn-primary ${isSaving ? 'loading' : ''}`}
                                                disabled={isSaving || submitting}
                                            >
                                                <Trans id="basefields:save" />
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-error ml-2"
                                                onClick={() => setShowResetConfirm(true)}
                                                disabled={isSaving}
                                            >
                                                <Trans id="basefields:reset" />
                                            </button>
                                        </div>
                                    </form>
                                );
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default MatchWhenRole(withI18n()(BaseFieldsEditor), 'admin');
